import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const API_KEY = process.env.REACT_APP_API_KEY || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    ...(API_KEY ? { 'X-API-Key': API_KEY } : {}),
  },
});

// OIDC token provider, set by AuthGate when Keycloak is active.
// Signature: async (forceRefresh?: boolean) => token | null
// It refreshes the token when it's near expiry (or forced), so requests never
// go out with a just-expired access token (the cause of intermittent 401s).
let _authTokenGetter = null;

export function setAuthTokenGetter(fn) {
  _authTokenGetter = fn;
}

// Request interceptor: attach a fresh Bearer token when available
api.interceptors.request.use(async (config) => {
  // A 401 retry already carries a freshly refreshed token; re-reading the
  // (possibly stale) cached one here would overwrite it
  if (_authTokenGetter && !config._retriedAuth) {
    try {
      const token = await _authTokenGetter(false);
      if (token) config.headers.Authorization = `Bearer ${token}`;
    } catch { /* unauthenticated; the 401 retry below recovers */ }
  }
  return config;
});

// Response interceptor: on 401, force a token refresh and retry once. This
// transparently recovers from an expired/renewing token instead of surfacing
// an error to the user.
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const orig = error.config;
    if (error.response?.status === 401 && _authTokenGetter && orig && !orig._retriedAuth) {
      orig._retriedAuth = true;
      try {
        const token = await _authTokenGetter(true);
        if (token) {
          orig.headers = orig.headers || {};
          orig.headers.Authorization = `Bearer ${token}`;
          return api(orig);
        }
      } catch { /* fall through to reject */ }
    }
    return Promise.reject(error);
  }
);

// Monitored Domains
export const getMonitoredDomains = () => api.get('/monitored-domains/');
export const addMonitoredDomain = (domainName) => api.post('/monitored-domains/', { domain_name: domainName });
export const updateMonitoredDomain = (id, data) => api.patch(`/monitored-domains/${id}`, data);
export const deleteMonitoredDomain = (id, deleteHistory = false) =>
  api.delete(`/monitored-domains/${id}`, { params: { delete_history: deleteHistory } });
export const getMonitoredDomainInfo = (id, refresh = false) =>
  api.get(`/monitored-domains/${id}/info`, { params: { refresh } });

// Allowlist (known-good domains, suppressed across all discovery paths)
export const getAllowlist = () => api.get('/allowlist/');
export const addAllowlistEntry = (domainName, note = null, suppressExisting = true) =>
  api.post('/allowlist/', { domain_name: domainName, note, suppress_existing: suppressExisting });
export const deleteAllowlistEntry = (id) => api.delete(`/allowlist/${id}`);

// Alerts
export const getAlerts = (params = {}) => api.get('/alerts/', { params });
export const bulkAlertStatus = (ids, status, notes = null) =>
  api.post('/alerts/bulk-status', { ids, status, ...(notes && { notes }) });
export const autoSuppressAlerts = (dryRun = true, maxRisk = null) =>
  api.post('/alerts/auto-suppress', { dry_run: dryRun, ...(maxRisk != null && { max_risk: maxRisk }) });
export const getCampaigns = () => api.get('/alerts/campaigns');

// Overview / live monitoring
export const getOverview = () => api.get('/stats/overview');

// Scans
export const triggerScan = () => api.post('/scans/trigger');
export const cancelScan = () => api.post('/scans/cancel');
export const getScanHistory = (config = {}) => api.get('/scans/history', config);
export const getScanStatus = (logCursor = 0, config = {}) =>
  api.get(`/scans/status?log_cursor=${logCursor}`, config);
export const clearAllDetections = () => api.delete('/scans/detections');
export const resetScanHistory = () => api.delete('/scans/history');
export const resetStreamCounter = () => api.delete('/scans/stream-counter');
export const resetEverything = () => api.delete('/scans/reset');
export const triggerReclassify = () => api.post('/scans/reclassify');

// Schedule
export const getSchedule = () => api.get('/scans/schedule');
export const updateSchedule = (data) => api.put('/scans/schedule', data);

// Notifications
export const getNotificationSettings = () => api.get('/notifications/settings');
export const updateNotificationSettings = (data) => api.put('/notifications/settings', data);

// Export
export const exportAlertsCsv = (params = {}) =>
  api.get('/alerts/export', { params, responseType: 'blob' });

// Notifications
export const testNotification = () => api.post('/notifications/test');

export default api;
